import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ContactSearchBar - Debounced search input for contact filtering
 *
 * Features:
 * - Debounced input (300ms) to avoid excessive API calls
 * - Clear button when search has value
 * - Accessibility support
 *
 * @component
 */
const ContactSearchBar = ({ value, onChange, onClear }) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value || '');

  // Debounced onChange callback
  const debouncedOnChange = useCallback(
    debounce(newValue => {
      onChange(newValue);
    }, 300),
    [onChange],
  );

  // Update local value and trigger debounced change
  const handleChange = event => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  // Clear search
  const handleClear = () => {
    setLocalValue('');
    onClear();
  };

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  return (
    <TextField
      value={localValue}
      onChange={handleChange}
      placeholder={t('contacts.search.placeholder', 'Search contacts...')}
      size="small"
      variant="filled"
      hiddenLabel
      sx={{ flex: 1, minWidth: 250 }}
      InputProps={{
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear} aria-label={t('contacts.search.clear', 'Clear search')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        sx: {
          borderRadius: 'var(--button-radius)',
          backgroundColor: 'var(--color-gray-50)',
          border: '1px solid var(--button-outline-border)',
          '&:hover': { backgroundColor: 'var(--color-gray-50)' },
          '&.Mui-focused': {
            backgroundColor: 'var(--color-white)',
            borderColor: 'var(--button-outline-border-hover)',
          },
        },
      }}
    />
  );
};

ContactSearchBar.propTypes = {
  /** Current search value */
  value: PropTypes.string,
  /** Callback when search value changes (debounced) */
  onChange: PropTypes.func.isRequired,
  /** Callback when clear button is clicked */
  onClear: PropTypes.func.isRequired,
};

export default ContactSearchBar;
